import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Cookie, Shield, BarChart3, Megaphone, Settings } from 'lucide-react';

export default function CookiePolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="cookie-policy-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Link 
            to="/" 
            className="text-white/60 hover:text-gold transition-colors flex items-center gap-2 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Înapoi la pagina principală
          </Link>
        </div>

        {/* Header */}
        <div className="glass rounded-2xl p-8 mb-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-gold to-gold-hover flex items-center justify-center">
              <Cookie className="w-7 h-7 text-navy" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-2xl md:text-3xl text-white">
                Politica de Cookies
              </h1>
              <p className="text-gold">VaAi.ro</p>
            </div>
          </div>
          <p className="text-white/60 text-sm">
            Ultima actualizare: februarie 2026
          </p>
        </div>

        {/* Content */}
        <div className="glass rounded-2xl p-8 space-y-8">
          {/* Intro */}
          <section>
            <h2 className="font-heading font-bold text-xl text-gold mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Ce sunt cookie-urile?
            </h2>
            <p className="text-white/80 leading-relaxed mb-4">
              Cookie-urile sunt fișiere text de mici dimensiuni care sunt stocate pe dispozitivul tău (computer, telefon, tabletă) atunci când vizitezi un site web. Acestea permit site-ului să-și amintească acțiunile și preferințele tale (cum ar fi limba, dimensiunea fontului și alte preferințe de afișare) pentru o anumită perioadă de timp, astfel încât să nu fie nevoie să le reintroduci de fiecare dată când revii pe site.
            </p>
            <p className="text-white/80 leading-relaxed">
              Cookie-urile nu pot accesa alte informații de pe dispozitivul tău și nu pot transmite viruși sau programe malițioase.
            </p>
          </section>

          {/* Types of cookies */}
          <section>
            <h2 className="font-heading font-bold text-xl text-gold mb-4">
              Tipuri de cookie-uri utilizate
            </h2>
            
            {/* Essential Cookies */}
            <div className="bg-white/5 rounded-xl p-6 mb-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                  <Settings className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-2">Cookie-uri Esențiale (Obligatorii)</h3>
                  <p className="text-white/70 text-sm mb-3">
                    Aceste cookie-uri sunt necesare pentru funcționarea de bază a site-ului și nu pot fi dezactivate. Ele permit funcții esențiale precum navigarea între pagini, accesul la zonele securizate și memorarea preferințelor de consimțământ.
                  </p>
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-white/60 text-xs font-mono">
                      • vaai_cookieconsent - memorează alegerea ta privind cookie-urile<br/>
                      • token - autentificare utilizator (dacă te-ai logat)<br/>
                      • sessionId - identificator sesiune pentru chat AI
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Analytics Cookies */}
            <div className="bg-white/5 rounded-xl p-6 mb-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-2">Cookie-uri de Analiză (Opționale)</h3>
                  <p className="text-white/70 text-sm mb-3">
                    Folosim <strong className="text-white">Google Analytics 4</strong> cu <strong className="text-white">Consent Mode v2</strong> pentru analiză anonimă a traficului. 
                    Aceasta înseamnă că, implicit, colectarea datelor este dezactivată până când îți dai acordul explicit.
                  </p>
                  <div className="bg-white/5 rounded-lg p-3 mb-3">
                    <p className="text-white/60 text-xs font-mono">
                      • _ga - identificator anonim pentru utilizator (durata: 2 ani)<br/>
                      • _ga_G-DJC9C3F45X - identificator sesiune GA4 (durata: 2 ani)<br/>
                      • _gid - identificator sesiune zilnică (durata: 24 ore)
                    </p>
                  </div>
                  <div className="bg-blue-500/10 rounded-lg p-3 border border-blue-500/20">
                    <p className="text-white/70 text-xs">
                      <strong className="text-blue-400">📊 Ce colectăm:</strong> Paginile vizitate, durata sesiunilor, tipul dispozitivului, țara (aproximativă). 
                      <strong className="text-white"> Nu colectăm</strong> date personale identificabile, adrese IP complete sau informații de contact.
                    </p>
                  </div>
                  <p className="text-white/50 text-xs mt-3">
                    Furnizor: Google LLC | Durată: până la 2 ani | 
                    <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline ml-1">
                      Politica de confidențialitate Google
                    </a>
                    {' | '}
                    <a href="https://support.google.com/analytics/answer/6004245" target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">
                      Despre Google Analytics
                    </a>
                  </p>
                </div>
              </div>
            </div>

            {/* Marketing Cookies */}
            <div className="bg-white/5 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                  <Megaphone className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-2">Cookie-uri de Marketing (Opționale)</h3>
                  <p className="text-white/70 text-sm mb-3">
                    Aceste cookie-uri sunt utilizate pentru a afișa reclame relevante pentru tine. De asemenea, limitează numărul de afișări ale unei reclame și ajută la măsurarea eficienței campaniilor publicitare.
                  </p>
                  <div className="bg-white/5 rounded-lg p-3">
                    <p className="text-white/60 text-xs font-mono">
                      • _fbp - Facebook Pixel (dacă este activ)<br/>
                      • IDE, DSID - Google Ads remarketing
                    </p>
                  </div>
                  <p className="text-white/50 text-xs mt-2">
                    Notă: În prezent, VaAi.ro nu utilizează cookie-uri de marketing. Această secțiune este inclusă pentru transparență în cazul utilizării viitoare.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* How to manage */}
          <section>
            <h2 className="font-heading font-bold text-xl text-gold mb-4">
              Cum poți gestiona cookie-urile?
            </h2>
            <p className="text-white/80 leading-relaxed mb-4">
              Ai control total asupra cookie-urilor non-esențiale. Iată cum le poți gestiona:
            </p>
            <ul className="text-white/80 leading-relaxed space-y-3 ml-4">
              <li className="flex items-start gap-2">
                <span className="text-gold">•</span>
                <span><strong className="text-white">Banner-ul de consimțământ:</strong> La prima vizită, vezi un banner cu opțiunile "Accept toate" sau "Refuz opționale". Poți alege rapid una dintre aceste opțiuni.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gold">•</span>
                <span><strong className="text-white">Setări granulare (⚙️):</strong> Apasă butonul "Setări" din banner pentru a alege individual ce categorii accepți (Analytics, Marketing). Cookie-urile esențiale rămân mereu active.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gold">•</span>
                <span><strong className="text-white">Butonul "Schimbă preferințele":</strong> După ce ai făcut o alegere, poți oricând să-ți modifici preferințele folosind butonul care apare în colțul din stânga jos al ecranului.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-gold">•</span>
                <span><strong className="text-white">Setările browser-ului:</strong> Majoritatea browserelor îți permit să controlezi cookie-urile prin setări. Poți șterge cookie-urile existente și bloca cookie-urile noi.</span>
              </li>
            </ul>
            
            {/* Consent Mode info box */}
            <div className="mt-6 p-4 bg-blue-500/10 rounded-xl border border-blue-500/20">
              <p className="text-white/80 text-sm mb-2">
                <strong className="text-blue-400">🔒 Google Consent Mode v2:</strong>
              </p>
              <p className="text-white/70 text-sm">
                Folosim tehnologia Google Consent Mode v2 care asigură că Google Analytics nu colectează date până când nu îți dai acordul explicit. 
                Când refuzi cookie-urile de analiză, scriptul GA4 este încărcat, dar nu trimite date identificabile și nu setează cookie-uri.
              </p>
            </div>
            
            <div className="mt-4 p-4 bg-gold/10 rounded-xl border border-gold/20">
              <p className="text-white/80 text-sm">
                <strong className="text-gold">⚠️ Atenție:</strong> Dezactivarea cookie-urilor esențiale poate afecta funcționarea corectă a site-ului. Anumite funcționalități (precum autentificarea sau chat-ul AI) pot să nu funcționeze fără acestea.
              </p>
            </div>
          </section>

          {/* Legal basis */}
          <section>
            <h2 className="font-heading font-bold text-xl text-gold mb-4">
              Temeiul legal
            </h2>
            <p className="text-white/80 leading-relaxed mb-4">
              Procesăm cookie-urile în conformitate cu:
            </p>
            <ul className="text-white/80 leading-relaxed space-y-2 ml-4">
              <li>• <strong className="text-white">Regulamentul (UE) 2016/679</strong> (GDPR) - pentru protecția datelor personale</li>
              <li>• <strong className="text-white">Directiva 2002/58/CE</strong> (ePrivacy) - pentru confidențialitatea comunicațiilor electronice</li>
              <li>• <strong className="text-white">Legea nr. 506/2004</strong> privind prelucrarea datelor cu caracter personal în sectorul comunicațiilor electronice</li>
            </ul>
            <p className="text-white/80 leading-relaxed mt-4">
              Cookie-urile esențiale sunt procesate în baza <strong className="text-white">interesului legitim</strong> (funcționarea site-ului). Cookie-urile de analiză și marketing sunt procesate doar cu <strong className="text-white">consimțământul tău explicit</strong>.
            </p>
          </section>

          {/* Contact */}
          <section>
            <h2 className="font-heading font-bold text-xl text-gold mb-4">
              Contact
            </h2>
            <p className="text-white/80 leading-relaxed">
              Pentru întrebări sau solicitări legate de cookie-uri și protecția datelor, ne poți contacta la:
            </p>
            <div className="mt-4 p-4 bg-white/5 rounded-xl">
              <p className="text-white/70 text-sm">
                <strong className="text-white">Email:</strong> <a href="mailto:contact@vaai.ro" className="text-gold hover:underline">contact@vaai.ro</a><br/>
                <strong className="text-white">Răspuns:</strong> Încercăm să răspundem în maxim 48 de ore lucrătoare
              </p>
            </div>
          </section>

          {/* Footer */}
          <div className="pt-8 border-t border-white/10 text-center">
            <p className="text-gold font-semibold">
              VaAi.ro – Vacanțe AI © 2026 Toate drepturile rezervate.
            </p>
            <p className="text-white/40 text-sm mt-2">
              Această politică poate fi actualizată periodic. Te rugăm să o verifici regulat.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
